import React, { useState } from 'react'
import { Modal, Form } from 'react-bootstrap'
import {
  useNotificationService,
  Button,
  useErrorService,
} from '@abroad/components'
import API from '../../utils/API'

const AppleCalendarModal = ({
  showModal,
  handleClose,
  onCalendarConnected,
}) => {
  const [appleFormData, setAppleFormData] = useState({
    appleId: '',
    appSpecificPassword: '',
  })
  const [isConnectingApple, setIsConnectingApple] = useState(false)
  const Notification = useNotificationService()
  const Error = useErrorService()
  const handleAppleFormChange = (e) => {
    const { name, value } = e.target
    setAppleFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateAppleForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const passwordRegex = /^[a-z]{4}-[a-z]{4}-[a-z]{4}-[a-z]{4}$/i

    if (!emailRegex.test(appleFormData.appleId)) {
      Notification.showNotification(
        'Please enter a valid email address for Apple ID',
        'danger',
      )
      return false
    }

    if (!passwordRegex.test(appleFormData.appSpecificPassword)) {
      Notification.showNotification(
        'App-specific password must be in format: xxxx-xxxx-xxxx-xxxx',
        'danger',
      )

      return false
    }

    return true
  }

  const handleAppleConnect = async () => {
    if (!validateAppleForm()) return

    setIsConnectingApple(true)
    try {
      await API.AdminAPI.scheduling.connectAppleCalendar(appleFormData)

      Notification.showNotification(
        'Apple calendar connected successfully!',
        'success',
      )

      // Call the callback to refresh connected calendars list
      if (onCalendarConnected) {
        onCalendarConnected()
      }

      handleCloseModal()
    } catch (error) {
      Error.showError(error)

      Notification.showNotification(
        'Failed to connect Apple calendar. Please check your credentials and try again.',
        'danger',
      )
    } finally {
      setIsConnectingApple(false)
    }
  }

  const handleCloseModal = () => {
    setAppleFormData({ appleId: '', appSpecificPassword: '' })
    handleClose()
  }

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      centered
      className='payment-modal apple-calendar-modal'>
      <Modal.Header className='payment-modal-header d-flex justify-content-center'>
        <h3 className='payment-modal-title mb-0'>Connect Apple Calendar</h3>
      </Modal.Header>
      <Modal.Body className='payment-modal-body'>
        <div className='connect-apple-calendar-container'>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label className='input-lable'>Apple ID (Email)</Form.Label>
              <Form.Control
                type='email'
                name='appleId'
                value={appleFormData.appleId}
                onChange={handleAppleFormChange}
                placeholder='Enter your Apple ID'
                className='input-typo text-dark-900'
                disabled={isConnectingApple}
              />
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label className='input-lable'>
                App-Specific Password
              </Form.Label>
              <Form.Control
                type='password'
                name='appSpecificPassword'
                value={appleFormData.appSpecificPassword}
                onChange={handleAppleFormChange}
                placeholder='Enter app-specific password (xxxx-xxxx-xxxx-xxxx)'
                className='input-typo text-dark-900'
                disabled={isConnectingApple}
              />
            </Form.Group>

            {/* Step-by-step Instructions */}
            <div className='bg-light border rounded p-3 mb-4'>
              <h6 className='input-lable mb-3'>
                How to Generate App-Specific Password:
              </h6>
              <ol className='mb-0'>
                <li className='mb-2'>
                  Go to{' '}
                  <a
                    href='https://appleid.apple.com/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-decoration-none text-primary'>
                    appleid.apple.com
                  </a>{' '}
                  and sign in with your Apple ID
                </li>
                <li className='mb-2'>
                  In the "Sign In and Security" section, click on "App-Specific
                  Passwords"
                </li>
                <li className='mb-2'>
                  Click the "+" button to generate a new password
                </li>
                <li className='mb-2'>
                  Enter a label for this password (e.g., "Abroad Calendar")
                </li>
                <li className='mb-2'>
                  Click "Create" and copy the generated password
                </li>
                <li className='mb-0'>
                  Paste the password in the field above (format:
                  xxxx-xxxx-xxxx-xxxx)
                </li>
              </ol>
              <div className='mt-3 p-2 bg-opacity-10 border border-dark rounded'>
                <small>
                  <strong>Note:</strong> You can only see this password once.
                  Make sure to copy it before closing the Apple ID page.
                </small>
              </div>
            </div>
          </Form>

          {/* Action Buttons */}
          <div className='d-flex justify-content-end'>
            <Button
              variant='secondary-light'
              onClick={handleCloseModal}
              className='font-montserrat border-radius-5 px-3 secondary-popup-button btn btn-secondary-light btn-sm mr-2'
              size='sm'
              disabled={isConnectingApple}>
              CANCEL
            </Button>
            <Button
              variant='saffron'
              onClick={() => {
                handleAppleConnect()
              }}
              className='font-montserrat border-radius-5 px-3 text-white'
              size='sm'
              disabled={isConnectingApple}>
              {isConnectingApple ? 'CONNECTING...' : 'CONNECT'}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AppleCalendarModal
